import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import { PROVISIONAL_OFFER_SLIDER } from '../../../../constants/heapEvents';
import { calculateLoanTerms } from '../../../../lib/calculateKfi';
import { track } from '../../../../lib/heapHelpers';
import { formatNumber, formatCurrencyNumber } from '../../../../lib/numberFormatter';
import WhiteCard from '../../../WhiteCard';
import OfferDetailsSection from '../OfferDetailsSection';
import RepaymentTermSelector from '../RepaymentTermSelector';

import './index.scss';

const CostOfLoan = ({
  explanationOpen,
  currency,
  offerAccepted,
  loanAmount,
  adminFee,
  adminFeePercentage,
  selectedOffer,
  processingFeeValue,
  applicationId,
  customerGuid,
  cosignerSelection,
  gracePeriodInMonths,
}) => {
  const explanationText = (
    <>
      Your loan costs include an admin fee and interest. The interest you pay may change during your
      loan term, depending on the variable portion of the interest rate and how fast you repay your
      loan.
    </>
  );

  const findRepaymentOption = () => {
    if (offerAccepted) {
      // if the offer is accepted there's only one option to use
      return {
        interestRepayable: formatCurrencyNumber(Math.ceil(selectedOffer.total_interest_charge)),
        monthlyRepayable: formatCurrencyNumber(Math.ceil(selectedOffer.monthly_repayment)),
        totalRepayable: formatCurrencyNumber(Math.ceil(selectedOffer.total_repayable_amount)),
      };
    } else {
      if (selectedOffer.term_in_months === repaymentTerm * 12) {
        return {
          interestRepayable: formatCurrencyNumber(Math.ceil(selectedOffer.total_interest_charge)),
          monthlyRepayable: formatCurrencyNumber(Math.ceil(selectedOffer.monthly_repayment)),
          totalRepayable: formatCurrencyNumber(Math.ceil(selectedOffer.total_repayable_amount)),
        };
      } else {
        const kfi = calculateLoanTerms(
          (baseRate + fixedMargin) / 100,
          selectedOffer.recalculation_requirements.disbursements,
          adminFee,
          repaymentTerm * 12,
          selectedOffer.recalculation_requirements.first_repayment_date,
          selectedOffer.grace_payment_amount,
          gracePeriodInMonths,
        );

        return {
          interestRepayable: formatCurrencyNumber(Math.ceil(kfi.totalInterest)),
          monthlyRepayable: formatCurrencyNumber(Math.ceil(kfi.monthlyRepayment)),
          totalRepayable: formatCurrencyNumber(Math.ceil(kfi.totalRepayable)),
        };
      }
    }
  };

  const fixedMargin = Number.parseFloat(selectedOffer.fixed_margin);

  const initialTerm = selectedOffer.term_in_months / 12;
  const [repaymentTerm, setRepaymentTerm] = useState(initialTerm);
  const initalBaseRate = Number.parseFloat(
    selectedOffer?.recalculation_requirements?.high_precision_base_rate || selectedOffer.base_rate,
  );
  const [baseRate, setBaseRate] = useState(initalBaseRate);

  const trackUpdateTerm = term => {
    setRepaymentTerm(term);
    track(PROVISIONAL_OFFER_SLIDER, {
      'Application ID': applicationId,
      'Customer GUID': customerGuid,
      'Length to repay': term * 12, // in months
    });
  };

  const isModified = () => {
    return repaymentTerm !== initialTerm || baseRate !== initalBaseRate;
  };

  const resetToInitialValues = useCallback(() => {
    setRepaymentTerm(initialTerm);
    setBaseRate(initalBaseRate);
  }, [initialTerm, initalBaseRate]);

  useEffect(() => {
    resetToInitialValues();
  }, [selectedOffer, resetToInitialValues]);

  return (
    <OfferDetailsSection
      className="cost-of-loan"
      explanationOpen={explanationOpen}
      title="Cost of loan"
      explanationText={explanationText}
    >
      <WhiteCard
        className={(isModified() ? 'cost-of-loan__wrapper-blue' : '') + ' cost-of-loan__white-card'}
      >
        <div className="cost-of-loan__wrapper">
          <div className="cost-of-loan__list">
            <ul>
              <li>
                <div className="cost-of-loan__list__label h6">Loan amount</div>
                <div className="cost-of-loan__list__value h6" data-testid="loan-amount-value">
                  {currency}&nbsp;<span className="h4">{formatCurrencyNumber(loanAmount)}</span>
                </div>
              </li>
              <li>
                <div className="cost-of-loan__list__label h6">
                  Admin fee ({formatNumber(adminFeePercentage)}%)
                </div>
                <div className="cost-of-loan__list__value h6" data-testid="admin-fee-value">
                  {currency}&nbsp;<span className="h4">{formatCurrencyNumber(adminFee)}</span>
                </div>
              </li>
              <li>
                <div className="cost-of-loan__list__label h6">Interest payable</div>
                <div className="cost-of-loan__list__value h6" data-testid="interest-payable-value">
                  {currency}&nbsp;
                  <span className="h4">{findRepaymentOption().interestRepayable}</span>
                </div>
              </li>
              <hr />
              <li className="total-repayable">
                <div className="cost-of-loan__list__label h6">
                  Total repayable
                  {processingFeeValue && <sup data-testid="processing-fee-mark">1</sup>}
                </div>
                <div className="cost-of-loan__list__value cost-of-loan__total-repayable h6">
                  <div data-testid="total-repayable-value">
                    {currency}&nbsp;
                    <span className="h4">{findRepaymentOption().totalRepayable}</span>
                  </div>
                  <div className="cost-of-loan__monthly-repayments-container">
                    {cosignerSelection === 'cosigner' && (
                      <>
                        <div
                          className="cost-of-loan__monthly-cosigner-repayments h6"
                          data-testid="monthly-cosigner-repayments-value"
                        >
                          {gracePeriodInMonths} &times;&nbsp;
                          {currency}&nbsp;
                          <span className="cost-of-loan__value h4">
                            {formatCurrencyNumber(selectedOffer.grace_payment_amount, 2, 2)}
                          </span>
                        </div>
                        <span className="monthly-repayments-plus">&#43;</span>
                      </>
                    )}
                    <div
                      className="cost-of-loan__monthly-repayments h6"
                      data-testid="monthly-repayments-value"
                    >
                      {repaymentTerm * 12} &times;&nbsp;
                      {currency}&nbsp;
                      <span className="cost-of-loan__value h4">
                        {findRepaymentOption().monthlyRepayable}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          {!offerAccepted && (
            <>
              <RepaymentTermSelector
                repaymentTerm={repaymentTerm}
                maximumRepaymentTerm={initialTerm}
                onRepaymentTermChange={trackUpdateTerm}
              />
              {isModified() && (
                <div className="cost-of-loan__simulation-notice">
                  <span className="h5">Simulation only</span>
                  <button className="action-buttons__accept" onClick={resetToInitialValues}>
                    Reset
                  </button>
                </div>
              )}
            </>
          )}
          {processingFeeValue && (
            <div className="cost-of-loan__footnote h6" data-testid="processing-fee-footnote">
              <sup>1</sup>&nbsp;excludes a {currency}&nbsp;
              {formatCurrencyNumber(processingFeeValue)} loan confirmation fee
            </div>
          )}
        </div>
      </WhiteCard>
    </OfferDetailsSection>
  );
};

CostOfLoan.propTypes = {
  explanationOpen: PropTypes.bool.isRequired,
  currency: PropTypes.string.isRequired,
  offerAccepted: PropTypes.bool.isRequired,
  loanAmount: PropTypes.string.isRequired,
  adminFee: PropTypes.string.isRequired,
  adminFeePercentage: PropTypes.string.isRequired,
  selectedOffer: PropTypes.shape({
    total_interest_charge: PropTypes.string.isRequired,
    monthly_repayment: PropTypes.string.isRequired,
    total_repayable_amount: PropTypes.string.isRequired,
    fixed_margin: PropTypes.string.isRequired,
    term_in_months: PropTypes.number.isRequired,
    base_rate: PropTypes.string.isRequired,
    grace_payment_amount: PropTypes.string.isRequired,
    recalculation_requirements: PropTypes.shape({
      disbursements: PropTypes.array.isRequired,
      first_repayment_date: PropTypes.string.isRequired,
      high_precision_base_rate: PropTypes.string,
    }).isRequired,
  }).isRequired,
  processingFeeValue: PropTypes.string,
  applicationId: PropTypes.string.isRequired,
  customerGuid: PropTypes.string.isRequired,
  cosignerSelection: PropTypes.string.isRequired,
  gracePeriodInMonths: PropTypes.number.isRequired,
};

export default CostOfLoan;
